import React, { useState, useEffect, useCallback } from 'react';
import { getProjects } from '../../services/projectService';
import { Alert, AlertDescription } from '../ui/alert';
import { PageLoading } from '../ui/Loading';
import { AlertCircle } from 'lucide-react';

// Import components
import DashboardStats from './DashboardStats';
import DashboardSummary from './DashboardSummary';
import DashboardPaymentSummary from './DashboardPaymentSummary';
import DashboardRecentProjects from './DashboardRecentProjects';

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({
    totalProjects: 0,
    completedProjects: 0,
    inProgressProjects: 0,
    pendingProjects: 0,
    totalRevenue: 0,
    totalPaid: 0,
    totalActualReceived: 0,
    totalVatAmount: 0,
    totalWithholdingTax: 0,
    pendingAmount: 0,
    totalCompanyFee: 0,
    totalSalesCommission: 0,
    totalDeveloperPayments: 0,
    totalOtherExpenses: 0,
    totalExpenses: 0,
    netProfit: 0,
    paidCompanyFee: 0,
    pendingCompanyFee: 0,
    paidSalesCommission: 0,
    pendingSalesCommission: 0,
    paidDeveloperPayments: 0,
    pendingDeveloperPayments: 0,
    paidOtherExpenses: 0,
    pendingOtherExpenses: 0,
    totalPaidExpenses: 0,
    totalPendingExpenses: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: projectsData, error: projectsError } = await getProjects();
      if (projectsError) throw projectsError;

      setProjects(projectsData || []);
      const calculatedStats = calculateStats(projectsData || []);
      setStats(calculatedStats);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const calculateStats = (projectsData) => {
    let totalProjects = projectsData.length;
    let completedProjects = 0;
    let inProgressProjects = 0;
    let pendingProjects = 0;
    let totalRevenue = 0;
    let totalPaid = 0;
    let totalActualReceived = 0;
    let totalVatAmount = 0;
    let totalWithholdingTax = 0;
    let totalCompanyFee = 0;
    let totalSalesCommission = 0;
    let totalDeveloperPayments = 0;
    let totalOtherExpenses = 0;
    
    // แยกรายจ่ายเป็นชำระแล้วและค้างชำระ
    let paidCompanyFee = 0;
    let pendingCompanyFee = 0;
    let paidSalesCommission = 0;
    let pendingSalesCommission = 0;
    let paidDeveloperPayments = 0;
    let pendingDeveloperPayments = 0;
    let paidOtherExpenses = 0;
    let pendingOtherExpenses = 0;

    projectsData.forEach(project => {
      // นับจำนวนโปรเจคตามสถานะ
      const paidAmount = project.totalPaidAmount || 0;
      const totalInstallmentAmount = project.totalPaymentAmount || 0;
      
      if (totalInstallmentAmount === 0) {
        pendingProjects++;
      } else if (paidAmount >= totalInstallmentAmount) {
        completedProjects++;
      } else if (paidAmount > 0) {
        inProgressProjects++;
      } else {
        pendingProjects++;
      }

      // รายได้รวม - ใช้ total_with_vat ถ้ามี หรือคำนวณจาก project_price + vat
      let projectRevenue = 0;
      if (project.total_with_vat) {
        projectRevenue = Number(project.total_with_vat);
      } else {
        const projectPrice = Number(project.project_price) || 0;
        const vatAmount = Number(project.vat?.amount) || 0;
        projectRevenue = projectPrice + vatAmount;
      }
      totalRevenue += projectRevenue;

      // เงินที่ชำระแล้ว (ยอดที่ยังไม่หัก WHT)
      totalPaid += paidAmount;

      // ยอดรายได้จริง (หัก VAT 7% และ WHT 3% ถ้ามี)
      const actualReceivedAmount = project.actual_received || 0;
      totalActualReceived += actualReceivedAmount;

      // ใช้ VAT amount จาก database
      let vatAmount = 0;
      try {
        const vat = typeof project.vat === 'string' ? JSON.parse(project.vat) : project.vat;
        vatAmount = vat?.amount || 0;
      } catch (e) {
        console.error('Error parsing vat:', e);
      }
      totalVatAmount += vatAmount;

      // ใช้ WHT amount จาก database
      let whtAmount = 0;
      try {
        const wht = typeof project.withholding_tax === 'string' ? JSON.parse(project.withholding_tax) : project.withholding_tax;
        whtAmount = wht?.amount || 0;
      } catch (e) {
        console.error('Error parsing withholding_tax:', e);
      }
      totalWithholdingTax += whtAmount;

      // ค่าดำเนินการบริษัท - ใช้ข้อมูลจาก database
      const companyFee = Number(project.total_company_fee_amount || 0);
      totalCompanyFee += companyFee;
      
      // ค่าดำเนินการบริษัทที่จ่ายแล้ว - คำนวณจากงวดการชำระที่ชำระแล้ว
      let paidCompanyFeeAmount = 0;
      if (project.company_fee?.installment_distribution && project.payment_schedule) {
        project.payment_schedule.forEach(payment => {
          if (payment.paid) {
            const distribution = project.company_fee.installment_distribution.find(
              dist => dist.installment === payment.installment_number
            );
            if (distribution) {
              if (project.company_fee.distribution_type === 'percentage') {
                paidCompanyFeeAmount += companyFee * (distribution.percentage || 0) / 100;
              } else {
                paidCompanyFeeAmount += distribution.amount || 0;
              }
            }
          }
        });
      }
      const pendingCompanyFeeAmount = companyFee - paidCompanyFeeAmount;
      paidCompanyFee += paidCompanyFeeAmount;
      pendingCompanyFee += pendingCompanyFeeAmount;

      // Commission Sale - ใช้ข้อมูลจาก database
      const salesCommission = Number(project.total_sales_commission_amount || 0);
      totalSalesCommission += salesCommission;
      
      const paidSalesCommissionAmount = Number(project.total_distributed_sales_commission || 0);
      const pendingSalesCommissionAmount = salesCommission - paidSalesCommissionAmount;
      paidSalesCommission += paidSalesCommissionAmount;
      pendingSalesCommission += pendingSalesCommissionAmount;

      // ค่าจ้าง Developer - ใช้ข้อมูลจาก database
      const devPayments = project.totalDevPaymentsAmount || 0;
      totalDeveloperPayments += devPayments;
      
      // ค่าจ้าง Developer ที่จ่ายแล้ว
      const paidDevPaymentsAmount = project.developer_payments?.reduce((sum, dev) => 
        sum + (dev.paid ? Number(dev.amount || 0) : 0), 0) || 0;
      const pendingDevPaymentsAmount = devPayments - paidDevPaymentsAmount;
      paidDeveloperPayments += paidDevPaymentsAmount;
      pendingDeveloperPayments += pendingDevPaymentsAmount;

      // ค่าใช้จ่ายโปรเจค - ใช้ข้อมูลจาก database
      const otherExpenses = project.totalOtherExpensesAmount || 0;
      totalOtherExpenses += otherExpenses;
      
      // ค่าใช้จ่ายโปรเจคที่จ่ายแล้ว
      const paidOtherExpensesAmount = project.other_expenses?.reduce((sum, expense) => 
        sum + (expense.paid ? Number(expense.amount || 0) : 0), 0) || 0;
      const pendingOtherExpensesAmount = otherExpenses - paidOtherExpensesAmount;
      paidOtherExpenses += paidOtherExpensesAmount;
      pendingOtherExpenses += pendingOtherExpensesAmount;
    });

    const pendingAmount = totalRevenue - totalPaid;
    
    // ค่าดำเนินการบริษัทเป็นรายได้ ไม่ใช่รายจ่าย
    const totalExpenses = totalSalesCommission + totalDeveloperPayments + totalOtherExpenses;
    const totalPaidExpenses = paidSalesCommission + paidDeveloperPayments + paidOtherExpenses;
    const totalPendingExpenses = pendingSalesCommission + pendingDeveloperPayments + pendingOtherExpenses;
    
    // กำไร = รายได้จริงที่ได้รับชำระแล้ว - รายจ่ายที่ชำระแล้ว
    // รายได้จริงที่ได้รับชำระแล้ว = (ยอดที่ชำระแล้ว - VAT ที่ชำระแล้ว - WHT ที่ชำระแล้ว)
    // คำนวณ VAT และ WHT ที่ชำระแล้วตามสัดส่วน
    const paidVatAmount = totalPaid > 0 ? (totalVatAmount * totalPaid / totalRevenue) : 0;
    const paidWhtAmount = totalPaid > 0 ? (totalWithholdingTax * totalPaid / totalRevenue) : 0;
    const paidAmountAfterVatWht = totalPaid - paidVatAmount - paidWhtAmount;
    const netProfit = paidAmountAfterVatWht - totalPaidExpenses;

    return {
      totalProjects,
      completedProjects,
      inProgressProjects,
      pendingProjects,
      totalRevenue,
      totalPaid,
      totalActualReceived,
      totalVatAmount,
      totalWithholdingTax,
      pendingAmount,
      totalCompanyFee,
      totalSalesCommission,
      totalDeveloperPayments,
      totalOtherExpenses,
      totalExpenses,
      netProfit,
      paidCompanyFee,
      pendingCompanyFee,
      paidSalesCommission,
      pendingSalesCommission,
      paidDeveloperPayments,
      pendingDeveloperPayments,
      paidOtherExpenses,
      pendingOtherExpenses,
      totalPaidExpenses,
      totalPendingExpenses,
      paidAmountAfterVatWht,
      paidVatAmount,
      paidWhtAmount
    };
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <PageLoading message="กำลังโหลดข้อมูล Dashboard..." />;
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          เกิดข้อผิดพลาด: {error}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          Dashboard
        </h1>
        <p className="text-muted-foreground mt-2">
          ภาพรวมการจัดการเงินโปรเจค
        </p>
      </div>
      
      {/* Payment Summary */}
      <DashboardPaymentSummary stats={stats} />

      {/* Stats Cards */}
      <DashboardStats stats={stats} />

      {/* Summary */}
      <DashboardSummary stats={stats} />

      {/* Recent Projects */}
      <DashboardRecentProjects projects={projects} />
    </div>
  );
};

export default Dashboard;
